import { Box } from "@mantine/core";
import { MaskedInput } from "@onengine/component-library";
import { IconAlertOctagon } from "@tabler/icons-react";
import clsx from "clsx";
import { Controller, type Control, type FieldPath, type FieldValues } from "react-hook-form";
import type { CSSProperties, HTMLInputTypeAttribute } from "react";
import styles from "./RHFMaskedInput.module.scss";

export type RHFMaskedInputProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  mask: string;
  testId?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  error?: string;
  defaultValue?: string;
  inputMode?: "tel" | "numeric" | "decimal" | "text" | "search" | "email" | "url" | "none";
  type?: HTMLInputTypeAttribute;
};

export function RHFMaskedInput<TFieldValues extends FieldValues>({
  name,
  control,
  mask,
  testId,
  label,
  placeholder,
  required,
  disabled,
  className,
  style,
  error,
  defaultValue = "",
  inputMode = "numeric",
  type,
}: RHFMaskedInputProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue as never}
      render={({ field, fieldState }) => {
        const message = error ?? fieldState.error?.message;
        return (
          <Box className={clsx(styles.wrapper, className)}>
            <MaskedInput
              testId={testId ?? name}
              label={label}
              placeholder={placeholder}
              mask={mask}
              required={required}
              disabled={disabled}
              classNames={{
                input: clsx(styles.input, !!message && styles.inputError),
                label: styles.label,
              }}
              onChange={(e) => field.onChange(e.currentTarget.value)}
              onBlur={field.onBlur}
              {...({
                value: field.value ?? "",
                name: field.name,
                style,
                inputMode,
                type,
                // react-imask never fires onChange for user typing — only onAccept.
                // Without this the Controller value stays empty no matter what is typed.
                onAccept: (value: unknown) => field.onChange(String(value ?? "")),
              } as Record<string, unknown>)}
            />
            {message && (
              <Box className={styles.errorMessage}>
                <IconAlertOctagon size={16} className={styles.errorIcon} />
                <span>{message}</span>
              </Box>
            )}
          </Box>
        );
      }}
    />
  );
}
